/**
 * BudgetLineItemDrawer Component
 * Add/edit budget line item form
 */

import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { X, DollarSign } from 'lucide-react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormInput,
  FormSelect,
  FormTextArea,
  CurrencyInput,
  NumberInput,
  SearchableDropdown,
} from '../forms';
import { useGetCostCodesQuery } from '../../services/costCodeApi';
import type { CostCode } from '../../types/costCode';
import type {
  BudgetLineItem,
  CreateLineItemRequest,
  UpdateLineItemRequest,
} from '../../types/budget';

export interface BudgetLineItemDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateLineItemRequest | UpdateLineItemRequest) => Promise<void>;
  lineItem?: BudgetLineItem | null;
  isLoading?: boolean;
  categories: string[];
  divisions: string[];
}

// Form data type
interface LineItemFormData {
  costCode: string;
  description: string;
  category: string;
  division?: string;
  unit?: string;
  quantity?: number;
  unitCost?: number;
  originalBudget: number;
  notes?: string;
}

// Validation schema
const lineItemSchema: Yup.ObjectSchema<LineItemFormData> = Yup.object({
  costCode: Yup.string()
    .required('Cost code is required')
    .max(20, 'Cost code must be 20 characters or less'),
  description: Yup.string()
    .required('Description is required')
    .max(255, 'Description must be 255 characters or less'),
  category: Yup.string().required('Category is required'),
  division: Yup.string().optional(),
  unit: Yup.string().optional(),
  quantity: Yup.number().min(0, 'Quantity must be positive').optional(),
  unitCost: Yup.number().min(0, 'Unit cost must be positive').optional(),
  originalBudget: Yup.number()
    .required('Original budget is required')
    .min(0, 'Budget must be positive'),
  notes: Yup.string().optional(),
}) as Yup.ObjectSchema<LineItemFormData>;

const BudgetLineItemDrawer: React.FC<BudgetLineItemDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  lineItem,
  isLoading = false,
  categories,
  divisions,
}) => {
  const isEditMode = !!lineItem;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LineItemFormData>({
    resolver: yupResolver(lineItemSchema) as any,
    defaultValues: {
      costCode: '',
      description: '',
      category: '',
      division: '',
      unit: '',
      quantity: undefined,
      unitCost: undefined,
      originalBudget: 0,
      notes: '',
    },
  });

  // Fetch cost codes for dropdown
  const { data: costCodesResponse } = useGetCostCodesQuery({ isActive: true, limit: 500 });
  const costCodeOptions = useMemo(() => {
    const costCodes = costCodesResponse?.data?.costCodes || [];
    return costCodes.map((cc: CostCode) => ({
      value: cc.code,
      label: `${cc.code} - ${cc.description}`,
    }));
  }, [costCodesResponse]);

  // Reset form when lineItem changes
  useEffect(() => {
    if (lineItem) {
      reset({
        costCode: lineItem.costCode,
        description: lineItem.description,
        category: lineItem.category,
        division: lineItem.division || '',
        unit: lineItem.unit || '',
        quantity: lineItem.quantity,
        unitCost: lineItem.unitCost,
        originalBudget: lineItem.originalBudget,
        notes: lineItem.notes || '',
      });
    } else {
      reset({
        costCode: '',
        description: '',
        category: '',
        division: '',
        unit: '',
        quantity: undefined,
        unitCost: undefined,
        originalBudget: 0,
        notes: '',
      });
    }
  }, [lineItem, reset]);

  const handleFormSubmit = async (data: LineItemFormData) => {
    try {
      await onSubmit(data as CreateLineItemRequest);
      onClose();
      reset();
    } catch (error) {
      console.error('Error submitting line item:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const categoryOptions = categories.map(cat => ({ value: cat, label: cat }));
  const divisionOptions = [
    { value: '', label: 'Select division (optional)' },
    ...divisions.map(div => ({ value: div, label: div })),
  ];

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Drawer - Large size for multiple sections */}
      <div className="fixed top-6 right-6 bottom-6 left-6 z-[100] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <DollarSign className="h-5 w-5 text-brand-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Edit Line Item' : 'Add Line Item'}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="costCode"
                    control={control}
                    render={({ field }) => (
                      <SearchableDropdown
                        label="Cost Code"
                        options={costCodeOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select cost code..."
                        error={errors.costCode?.message}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <FormSelect
                        label="Category"
                        options={categoryOptions}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.category}
                        required
                        placeholder="Select category"
                      />
                    )}
                  />
                </div>

                <FormInput
                  label="Description"
                  {...register('description')}
                  error={errors.description}
                  required
                  placeholder="Line item description"
                />

                <Controller
                  name="division"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      label="Division"
                      options={divisionOptions}
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.division}
                      placeholder="Select division (optional)"
                    />
                  )}
                />
              </div>

              {/* Budget Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Budget Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormInput
                    label="Unit"
                    {...register('unit')}
                    error={errors.unit}
                    placeholder="e.g., SF, LF, EA"
                  />

                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        label="Quantity"
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.quantity?.message}
                        placeholder="0"
                        min={0}
                      />
                    )}
                  />

                  <Controller
                    name="unitCost"
                    control={control}
                    render={({ field }) => (
                      <CurrencyInput
                        label="Unit Cost"
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.unitCost?.message}
                        placeholder="$0.00"
                      />
                    )}
                  />
                </div>

                <Controller
                  name="originalBudget"
                  control={control}
                  render={({ field }) => (
                    <CurrencyInput
                      label="Original Budget"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.originalBudget?.message}
                      required
                      placeholder="$0.00"
                    />
                  )}
                />
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Additional Information
                </h3>

                <FormTextArea
                  label="Notes"
                  {...register('notes')}
                  error={errors.notes}
                  placeholder="Add any additional notes or comments..."
                  rows={4}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Saving...' : isEditMode ? 'Update Line Item' : 'Add Line Item'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default BudgetLineItemDrawer;
